import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  BookOpen, 
  Video, 
  GraduationCap, 
  Download, 
  Play, 
  Trophy, 
  Lock, 
  Crown,
  FileText,
  CheckCircle,
  Clock,
  Star,
  X,
  Maximize,
  Settings,
  Plus,
  Eye,
  Mail,
  Save,
  RefreshCw,
  AlertCircle,
  Gavel
} from 'lucide-react';

const SovereignToolbox = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Get tab from URL parameter, default to videos if not specified
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'videos');
  const [completedQuizzes, setCompletedQuizzes] = useState([]);
  const [quizScores, setQuizScores] = useState({});
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoPlayerOpen, setIsVideoPlayerOpen] = useState(false);
  
  // Document preview state for main SovereignToolbox component
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isDocumentPreviewOpen, setIsDocumentPreviewOpen] = useState(false);

  // Check if user has premium access (full Sovereign Toolbox) - includes all premium plans
  const hasPremiumAccess = user?.subscription_plan === 'sovereign_shield' || 
                           user?.subscription_plan === 'defender_of_the_people' || 
                           user?.subscription_plan === 'justice_seeker' ||
                           user?.subscription_plan === 'lifetime' ||
                           user?.role === 'admin';
  
  // Check if user has limited Sovereign Toolbox access (Pro Se Starter gets Video Vault and Study & Quizzes)
  const hasLimitedAccess = user?.subscription_plan === 'pro_se_starter';
  
  // Check if user has any Sovereign Toolbox access
  const hasSovereignAccess = hasPremiumAccess || hasLimitedAccess;
  
  // Pro Se Starter users can access Video Vault and Study & Quizzes but NOT Digital Library
  const hasVideoAccess = hasPremiumAccess || hasLimitedAccess;
  const hasQuizAccess = hasPremiumAccess || hasLimitedAccess;
  const hasDigitalLibraryAccess = hasPremiumAccess; // Only premium users get Digital Library
  
  // Pro Se Starter users get limited access to Remedy Generator (only 2 specific templates)
  const hasRemedyGeneratorAccess = hasPremiumAccess || hasLimitedAccess;
  const hasLimitedRemedyAccess = hasLimitedAccess && !hasPremiumAccess; // Pro Se Starter users only

  // Update active tab when URL parameter changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Load completed quizzes from localStorage
  useEffect(() => {
    if (hasSovereignAccess) {
      const completed = JSON.parse(localStorage.getItem(`quizzes_${user?.email}`) || '[]');
      const scores = JSON.parse(localStorage.getItem(`quiz_scores_${user?.email}`) || '{}');
      setCompletedQuizzes(completed);
      setQuizScores(scores);
    }
  }, [user?.email, hasSovereignAccess]);

  // Digital Library Content
  const libraryContent = {
    'Trust & Estate Law': [
      {
        title: 'The Emergency Banking Act & Your Equitable Remedy!',
        description: 'Understanding the Emergency Banking Act and available equitable remedies.',
        url: 'https://customer-assets.emergentagent.com/job_sovereign-ai-8/artifacts/52fkhosi_The_Emergency_Banking_Act_%26_Your_Equitable_Remedy%21.pdf',
        category: 'Trust & Estate Law',
        pages: 'New Release'
      }
    ],
    'Equity Jurisprudence': [
      {
        title: 'The Administrative Process in Equity Jurisprudence',
        description: 'Comprehensive guide to administrative processes within equity jurisprudence.',
        url: 'https://customer-assets.emergentagent.com/job_sovereign-ai-8/artifacts/hfu5f59r_The_Administrative_Process_in_Equity_Jurisprudence%20%281%29.pdf',
        category: 'Equity Jurisprudence',
        pages: 'New Release'
      },
      {
        title: 'Real-World Legal Applications of Equity Maxims',
        description: 'Practical applications of equity maxims in real-world legal scenarios.',
        url: 'https://customer-assets.emergentagent.com/job_sovereign-ai-8/artifacts/kkga9xpb_Real-World%20Legal%20Applications%20of%20Equity%20Maxims.pdf',
        category: 'Equity Jurisprudence', 
        pages: 'New Release'
      },
      {
        title: 'Categorized Collection of Maxims of Equity',
        description: 'Organized collection of equity maxims categorized by legal application.',
        url: 'https://customer-assets.emergentagent.com/job_sovereign-ai-8/artifacts/vge74816_Categorized%20Collection%20of%20Maxims%20of%20Equity.pdf',
        category: 'Equity Jurisprudence',
        pages: 'New Release'
      },
      {
        title: 'Notice of Rescission & Staying in Honor',
        description: 'Essential guide to rescission processes and maintaining honor in equity while protecting your legal standing.',
        url: 'https://customer-assets.emergentagent.com/job_10a0cade-cd56-4171-892d-941854d759d6/artifacts/q58tqu3t_Notice_of_Rescission_%26_Staying_in_Honor.pdf',
        category: 'Equity Jurisprudence',
        pages: 'New Release'
      }
    ],
    'Government & Administrative': [
      {
        title: 'Manual on FOIA and FARA Tools of Remedy in Equity and Sovereignty',
        description: 'Comprehensive guide to Freedom of Information Act (FOIA) and Foreign Agents Registration Act (FARA) as tools for remedy in equity and sovereignty.',
        url: 'https://customer-assets.emergentagent.com/job_sovereign-law/artifacts/u01nka46_Manual_on_FOIA_and_FARA_Tools_of_Remedy_in_Equity_and_Sovereignty%20%282%29.pdf',
        category: 'Government & Administrative',
        pages: 'New Release'
      }
    ],
    'Postal Authority & Private Equity': [
      {
        title: 'The Sovereign\'s Manual on Stamps, Postal Authority & Private Equity Process',
        description: 'Complete guide to using postal stamps, establishing postal authority, and understanding private equity processes for sovereign individuals.',
        url: 'https://customer-assets.emergentagent.com/job_sovereign-law/artifacts/h62uajkb_The%20Sovereign%E2%80%99s%20Manual%20on%20Stamps%2C%20Postal%20Authority%20%26%20Private%20Equity%20Process.pdf',
        category: 'Postal Authority & Private Equity',
        pages: 'New Release'
      }
    ]
  };

  // Video Vault Content
  const videoContent = [
    {
      id: 'Tkr7Uc1RtHw',
      title: 'Foundational Sovereignty Principles',
      description: 'Essential concepts every sovereign should understand.',
      category: 'Foundation',
      duration: '45 min'
    },
    {
      id: 'oJiNqefHgDw',
      title: 'Advanced Trust Strategies',
      description: 'Deep dive into trust creation and administration.',
      category: 'Trust Setup & Administration',
      duration: '38 min'
    },
    {
      id: 'nkXby1g7Aec',
      title: 'Courtroom Strategy & Tactics',
      description: 'Proven techniques for courtroom success.',
      category: 'Courtroom Roleplay Tutorials',
      duration: '52 min'
    }
  ];

  // Quiz Content - Enhanced with 10+ questions each
  const quizData = [
    {
      id: 'sovereignty_basics',
      title: 'Sovereignty Fundamentals',
      category: 'Foundation',
      questions: [
        {
          question: 'What is the foundation of Common Law?',
          type: 'multiple',
          options: ['Statutes', 'Natural Law', 'Administrative Code', 'Maritime Law'],
          correct: 1
        },
        {
          question: 'The Magna Carta was signed in what year?',
          type: 'multiple',
          options: ['1215', '1225', '1235', '1245'],
          correct: 0
        },
        {
          question: 'Equity follows the law.',
          type: 'boolean',
          correct: true
        },
        {
          question: 'Private capacity operations require no government permission.',
          type: 'boolean',
          correct: true
        },
        {
          question: 'Article I, Section 10, Clause 1 of the Constitution prohibits states from:',
          type: 'multiple',
          options: ['Regulating commerce', 'Impairing the obligation of contracts', 'Collecting taxes', 'Creating courts'],
          correct: 1
        },
        {
          question: 'The term "person" in legal documents always refers to a natural human being.',
          type: 'boolean',
          correct: false
        },
        {
          question: 'What is the primary maxim of equity regarding remedies?',
          type: 'multiple',
          options: ['Equity will not suffer a wrong to be without a remedy', 'Equity follows the law', 'He who seeks equity must do equity', 'Delay defeats equity'],
          correct: 0
        },
        {
          question: 'Administrative law supersedes Constitutional law.',
          type: 'boolean',
          correct: false
        },
        {
          question: 'The term "United States" can refer to:',
          type: 'multiple',
          options: ['Only the federal government', 'Only the 50 states', 'Both the federal government and territories', 'Only incorporated territories'],
          correct: 2
        },
        {
          question: 'A sovereign can waive their sovereignty through contract.',
          type: 'boolean',
          correct: true
        },
        {
          question: 'What type of law governs contracts between private parties?',
          type: 'multiple',
          options: ['Administrative Law', 'Statutory Law', 'Common Law', 'Maritime Law'],
          correct: 2
        },
        {
          question: 'Due process requires both procedural and substantive fairness.',
          type: 'boolean',
          correct: true
        }
      ]
    },
    {
      id: 'equity_law',
      title: 'Equity & Jurisprudence',
      category: 'Advanced',
      questions: [
        {
          question: 'Which maxim states "He who seeks equity must do equity"?',
          type: 'multiple',
          options: ['First Maxim', 'Second Maxim', 'Third Maxim', 'Fourth Maxim'],
          correct: 0
        },
        {
          question: 'Equity will not suffer a wrong to be without remedy.',
          type: 'boolean',
          correct: true
        },
        {
          question: 'Administrative discharge requires what type of acceptance?',
          type: 'multiple',
          options: ['Unconditional', 'Conditional', 'Partial', 'Complete'],
          correct: 1
        },
        {
          question: 'Equity acts in personam (against the person).',
          type: 'boolean',
          correct: true
        },
        {
          question: 'What is the primary purpose of a conditional acceptance?',
          type: 'multiple',
          options: ['To reject an offer', 'To accept with conditions', 'To create controversy', 'To delay proceedings'],
          correct: 1
        },
        {
          question: 'Laches is the equitable doctrine of delay defeating equity.',
          type: 'boolean',
          correct: true
        },
        {
          question: 'In equity, what is the remedy for breach of trust?',
          type: 'multiple',
          options: ['Monetary damages only', 'Specific performance', 'Both monetary and equitable relief', 'Criminal prosecution'],
          correct: 2
        },
        {
          question: 'Equity looks to the intent rather than the form.',
          type: 'boolean',
          correct: true
        },
        {
          question: 'What is required for an equitable estoppel claim?',
          type: 'multiple',
          options: ['Detrimental reliance', 'Written contract', 'Monetary consideration', 'Court approval'],
          correct: 0
        },
        {
          question: 'Clean hands doctrine requires the plaintiff to have acted fairly.',
          type: 'boolean',
          correct: true
        },
        {
          question: 'Unconscionable contracts can be voided in equity for:',
          type: 'multiple',
          options: ['Being too expensive', 'Procedural or substantive unfairness', 'Lack of consideration', 'Improper signatures'],
          correct: 1
        },
        {
          question: 'Equity will not perfect an imperfect gift.',
          type: 'boolean',
          correct: true
        }
      ]
    },
    {
      id: 'trust_law',
      title: 'Trust Law Essentials',
      category: 'Trust & Estate',
      questions: [
        {
          question: 'Who manages the trust assets?',
          type: 'multiple',
          options: ['Beneficiary', 'Trustee', 'Grantor', 'Court'],
          correct: 1
        },
        {
          question: 'A trust can exist without a written document.',
          type: 'boolean',
          correct: true
        },
        {
          question: 'Express trusts require explicit creation.',
          type: 'boolean',
          correct: true
        },
        {
          question: 'What are the three certainties required for a valid trust?',
          type: 'multiple',
          options: ['Subject, object, intention', 'Assets, beneficiaries, purpose', 'Grantor, trustee, beneficiary', 'All of the above'],
          correct: 3
        },
        {
          question: 'A trustee has a fiduciary duty to the beneficiaries.',
          type: 'boolean',
          correct: true
        },
        {
          question: 'What type of trust is created by operation of law?',
          type: 'multiple',
          options: ['Express trust', 'Constructive trust', 'Testamentary trust', 'Charitable trust'],
          correct: 1
        },
        {
          question: 'The same person can be both grantor and beneficiary of a trust.',
          type: 'boolean',
          correct: true
        },
        {
          question: 'What is the rule against perpetuities designed to prevent?',
          type: 'multiple',
          options: ['Tax avoidance', 'Property remaining in trust forever', 'Multiple beneficiaries', 'Charitable giving'],
          correct: 1
        },
        {
          question: 'A spendthrift clause protects trust assets from beneficiary creditors.',
          type: 'boolean',
          correct: true
        },
        {
          question: 'In a discretionary trust, who decides on distributions?',
          type: 'multiple',
          options: ['The beneficiary', 'The court', 'The trustee', 'The grantor'],
          correct: 2
        },
        {
          question: 'Trust income is always taxed to the trust entity.',
          type: 'boolean',
          correct: false
        },
        {
          question: 'What is a pour-over will?',
          type: 'multiple',
          options: ['A will that creates a trust', 'A will that transfers assets to an existing trust', 'A will that revokes a trust', 'A will written in multiple languages'],
          correct: 1
        }
      ]
    }
  ];

  const handleQuizCompletion = (quizId, score, total) => {
    const newCompleted = [...completedQuizzes];
    if (!newCompleted.includes(quizId)) {
      newCompleted.push(quizId);
    }
    
    const newScores = { ...quizScores, [quizId]: { score, total, date: new Date().toISOString() } };
    
    setCompletedQuizzes(newCompleted);
    setQuizScores(newScores);
    
    localStorage.setItem(`quizzes_${user?.email}`, JSON.stringify(newCompleted));
    localStorage.setItem(`quiz_scores_${user?.email}`, JSON.stringify(newScores));
  };

  const openVideoPlayer = (video) => {
    setSelectedVideo(video);
    setIsVideoPlayerOpen(true);
  };

  const closeVideoPlayer = () => {
    setSelectedVideo(null);
    setIsVideoPlayerOpen(false);
  };

  // Document preview functions for main SovereignToolbox component
  const openDocumentPreview = (document) => {
    console.log('Main SovereignToolbox openDocumentPreview called with:', document);
    setSelectedDocument(document);
    setIsDocumentPreviewOpen(true);
  };

  const closeDocumentPreview = () => {
    setSelectedDocument(null);
    setIsDocumentPreviewOpen(false);
  };

  // Download functions for PDF and DOC
  const downloadAsPDF = async (content, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL}/api/documents/download-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          content: content,
          filename: filename
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${filename}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('PDF generation failed');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const downloadAsDOCX = async (content, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL}/api/documents/download-docx`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          content: content,
          filename: filename
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${filename}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('DOCX generation failed');
      }
    } catch (error) {
      console.error('Error downloading DOCX:', error);
      alert('Error generating DOCX. Please try again.');
    }
  };

  if (!hasSovereignAccess) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-indigo-700 flex items-center justify-center">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Sovereign Toolbox</h1>
              <p className="text-sm text-gray-600">Premium Educational Resource Center</p>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-lg">
            <Lock className="w-16 h-16 text-gray-400 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Premium Content Locked
            </h2>
            <p className="text-gray-600 mb-8">
              The Sovereign Toolbox is exclusively available to Justice Seeker, Sovereign Shield and Defender of the People subscribers. 
              Unlock access to our comprehensive digital library, video vault, and study materials.
            </p>
            
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-gray-900 mb-3">What's Inside:</h3>
              <div className="grid grid-cols-1 gap-3 text-sm text-gray-700">
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-4 h-4 text-purple-600" />
                  <span>Comprehensive Digital Library with eBooks & Guides</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Video className="w-4 h-4 text-purple-600" />
                  <span>Exclusive Video Vault with Expert Lectures</span>
                </div>
                <div className="flex items-center space-x-2">
                  <GraduationCap className="w-4 h-4 text-purple-600" />
                  <span>Interactive Study Materials & Knowledge Quizzes</span>
                </div>
              </div>
            </div>

            <Button 
              className="bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 text-white px-8 py-3"
              onClick={() => window.location.href = '/#pricing'}
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade Today for Premium Access
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-indigo-700 flex items-center justify-center">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sovereign Toolbox</h1>
            <p className="text-sm text-gray-600">Your Digital War Chest of Remedy, Rights, and Revelation</p>
          </div>
          <Badge className={`ml-auto ${hasPremiumAccess ? 'bg-purple-100 text-purple-700' : 'bg-yellow-100 text-yellow-700'}`}>
            <Star className="w-3 h-3 mr-1" />
            {hasPremiumAccess ? 'Premium Access' : 'Limited Access'}
          </Badge>
        </div>
      </div>

      {hasLimitedAccess && (
        <div className="mx-6 mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-yellow-800">Justice Seeker Access</h3>
              <p className="text-sm text-yellow-700 mt-1">
                Your plan includes access to the Video Vault and Study & Quizzes. 
                Upgrade to Sovereign Shield for full access to Digital Library and Remedy Generator.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          <TabsList className={`grid w-full mt-4 px-2 md:px-6 grid-cols-3 gap-1 md:gap-2`}>
            {hasDigitalLibraryAccess && (
              <TabsTrigger value="library" className="flex flex-col md:flex-row items-center space-y-1 md:space-y-0 md:space-x-2 text-xs md:text-sm p-2 md:p-3">
                <BookOpen className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                <span className="text-center md:text-left leading-tight">Digital Library</span>
              </TabsTrigger>
            )}
            {hasVideoAccess && (
              <TabsTrigger value="videos" className="flex flex-col md:flex-row items-center space-y-1 md:space-y-0 md:space-x-2 text-xs md:text-sm p-2 md:p-3">
                <Video className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                <span className="text-center md:text-left leading-tight">Video Vault</span>
              </TabsTrigger>
            )}
            {hasQuizAccess && (
              <TabsTrigger value="study" className="flex flex-col md:flex-row items-center space-y-1 md:space-y-0 md:space-x-2 text-xs md:text-sm p-2 md:p-3">
                <GraduationCap className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                <span className="text-center md:text-left leading-tight">Study & Quizzes</span>
              </TabsTrigger>
            )}
          </TabsList>

          <div className="flex-1 overflow-y-auto p-6">
            {hasDigitalLibraryAccess && (
              <TabsContent value="library" className="mt-0 space-y-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Digital Library</h2>
                <p className="text-gray-600">"The law is written — and now, you can read it with eyes wide open."</p>
              </div>

              {Object.entries(libraryContent).map(([category, books]) => (
                <div key={category} className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                    {category}
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    {books.map((book, index) => (
                      <Card key={index} className="hover:shadow-lg transition-shadow duration-200">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3">
                              <div className="w-12 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded flex items-center justify-center">
                                <FileText className="w-6 h-6 text-white" />
                              </div>
                              <div className="flex-1">
                                <CardTitle className="text-lg text-gray-900 mb-1">{book.title}</CardTitle>
                                <p className="text-sm text-gray-600 mb-2">{book.description}</p>
                                <Badge variant="outline" className="text-xs">
                                  {book.pages}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <Button 
                            className="w-full bg-red-600 hover:bg-red-700 text-white"
                            onClick={() => window.open(book.url, '_blank')}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
              </TabsContent>
            )}

            <TabsContent value="videos" className="mt-0 space-y-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Video Vault</h2>
                <p className="text-gray-600">"See it. Hear it. Know it. Apply it."</p>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {videoContent.map((video, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow duration-200">
                    <CardHeader>
                      <div className="flex items-start space-x-4">
                        <div className="relative cursor-pointer" onClick={() => openVideoPlayer(video)}>
                          <img 
                            src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                            alt={video.title}
                            className="w-32 h-20 object-cover rounded hover:opacity-80 transition-opacity"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors">
                              <Play className="w-4 h-4 text-white ml-0.5" />
                            </div>
                          </div>
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg text-gray-900 mb-1">{video.title}</CardTitle>
                          <p className="text-sm text-gray-600 mb-2">{video.description}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <Badge variant="outline">{video.category}</Badge>
                            <span className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {video.duration}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Button 
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => openVideoPlayer(video)}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Watch Video
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="study" className="mt-0 space-y-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Study Material & Quizzes</h2>
                <p className="text-gray-600">"Test yourself before they test you."</p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {quizData.map((quiz) => (
                  <QuizCard 
                    key={quiz.id} 
                    quiz={quiz} 
                    isCompleted={completedQuizzes.includes(quiz.id)}
                    score={quizScores[quiz.id]}
                    onComplete={handleQuizCompletion}
                  />
                ))}
              </div>
            </TabsContent>

          </div>
        </Tabs>
      </div>

      {/* Integrated Video Player Modal */}
      {isVideoPlayerOpen && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{selectedVideo.title}</h3>
                <p className="text-sm text-gray-600">{selectedVideo.description}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeVideoPlayer}
                className="ml-4 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="relative bg-black" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1&rel=0&modestbranding=1`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            
            <div className="p-4 bg-gray-50">
              <div className="flex items-center space-x-6 text-sm text-gray-600">
                <span className="flex items-center">
                  <Video className="w-4 h-4 mr-1" />
                  {selectedVideo.category}
                </span>
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {selectedVideo.duration}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`https://youtu.be/${selectedVideo.id}`, '_blank')}
                  className="ml-auto"
                >
                  <Maximize className="w-4 h-4 mr-1" />
                  Open in YouTube
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal for main SovereignToolbox component */}
      {isDocumentPreviewOpen && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{selectedDocument.templateTitle}</h3>
                <p className="text-sm text-gray-600">
                  Created: {new Date(selectedDocument.createdAt).toLocaleDateString()}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeDocumentPreview}
                className="ml-4 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 max-h-96 overflow-y-auto">
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedDocument.generatedContent || 'Document content loading...' }}
              />
            </div>
            
            <div className="p-4 bg-gray-50 border-t">
              <div className="flex items-center justify-end space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => downloadAsPDF(selectedDocument.generatedContent, `${selectedDocument.templateTitle.replace(/[^a-zA-Z0-9]/g, '_')}`)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => downloadAsDOCX(selectedDocument.generatedContent, `${selectedDocument.templateTitle.replace(/[^a-zA-Z0-9]/g, '_')}`)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download DOCX
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Quiz Card Component
const QuizCard = ({ quiz, isCompleted, score, onComplete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);

  // Shuffle questions when quiz opens
  useEffect(() => {
    if (isOpen && quiz.questions) {
      const shuffled = [...quiz.questions].sort(() => Math.random() - 0.5);
      setShuffledQuestions(shuffled.slice(0, Math.min(10, shuffled.length))); // Show 10 questions per quiz
    }
  }, [isOpen, quiz.questions]);

  const handleAnswer = (questionIndex, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };

  const calculateScore = () => {
    let correct = 0;
    shuffledQuestions.forEach((question, index) => {
      const userAnswer = answers[index];
      if (question.type === 'multiple') {
        if (userAnswer === question.correct) correct++;
      } else if (question.type === 'boolean') {
        if (userAnswer === question.correct) correct++;
      }
    });
    return correct;
  };

  const submitQuiz = () => {
    const correctAnswers = calculateScore();
    const total = shuffledQuestions.length;
    setShowResults(true);
    onComplete(quiz.id, correctAnswers, total);
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResults(false);
    setIsOpen(false);
  };

  if (isOpen) {
    if (showResults) {
      const correctAnswers = calculateScore();
      const total = shuffledQuestions.length;
      const percentage = Math.round((correctAnswers / total) * 100);
      
      return (
        <Card className="border-2 border-blue-200">
          <CardHeader className="text-center">
            <CardTitle className="text-xl text-gray-900">Quiz Results</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="text-6xl font-bold text-blue-600">{percentage}%</div>
            <p className="text-lg text-gray-700">
              You scored {correctAnswers} out of {total} questions correctly
            </p>
            
            {percentage >= 80 && (
              <div className="flex items-center justify-center space-x-2 text-green-600">
                <Trophy className="w-5 h-5" />
                <span className="font-semibold">Excellent Work!</span>
              </div>
            )}
            
            <div className="space-x-2">
              <Button onClick={resetQuiz} variant="outline">
                Close
              </Button>
              <Button onClick={() => {
                setCurrentQuestionIndex(0);
                setAnswers({});
                setShowResults(false);
                const shuffled = [...quiz.questions].sort(() => Math.random() - 0.5);
                setShuffledQuestions(shuffled.slice(0, Math.min(10, shuffled.length)));
              }}>
                Retake Quiz
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    const currentQuestion = shuffledQuestions[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === shuffledQuestions.length - 1;
    const allAnswered = Object.keys(answers).length === shuffledQuestions.length;

    return (
      <Card className="border-2 border-blue-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-gray-900">{quiz.title}</CardTitle>
            <Badge variant="outline">
              Question {currentQuestionIndex + 1} of {shuffledQuestions.length}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {currentQuestion && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {currentQuestion.question}
              </h3>
              
              {currentQuestion.type === 'multiple' ? (
                <div className="space-y-2">
                  {currentQuestion.options.map((option, optionIndex) => (
                    <button
                      key={optionIndex}
                      className={`w-full text-left p-3 rounded border-2 transition-colors ${
                        answers[currentQuestionIndex] === optionIndex
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleAnswer(currentQuestionIndex, optionIndex)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    className={`w-full text-left p-3 rounded border-2 transition-colors ${
                      answers[currentQuestionIndex] === true
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleAnswer(currentQuestionIndex, true)}
                  >
                    True
                  </button>
                  <button
                    className={`w-full text-left p-3 rounded border-2 transition-colors ${
                      answers[currentQuestionIndex] === false
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleAnswer(currentQuestionIndex, false)}
                  >
                    False
                  </button>
                </div>
              )}
            </div>
          )}
          
          <div className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
              disabled={currentQuestionIndex === 0}
            >
              Previous
            </Button>
            
            {isLastQuestion ? (
              <Button 
                onClick={submitQuiz}
                disabled={!allAnswered}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Submit Quiz
              </Button>
            ) : (
              <Button 
                onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                disabled={answers[currentQuestionIndex] === undefined}
              >
                Next
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg text-gray-900 mb-1">{quiz.title}</CardTitle>
              <Badge variant="outline" className="text-xs mb-2">
                {quiz.category}
              </Badge>
              <p className="text-sm text-gray-600">
                {quiz.questions.length} questions • Multiple choice & True/False • 10 random questions per attempt
              </p>
            </div>
          </div>
          {isCompleted && (
            <div className="text-right">
              <CheckCircle className="w-5 h-5 text-green-500 mb-1" />
              {score && (
                <p className="text-xs text-gray-600">
                  Last Score: {score.score}/{score.total}
                </p>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Button 
          className="w-full"
          onClick={() => setIsOpen(true)}
        >
          {isCompleted ? 'Retake Quiz' : 'Start Quiz'}
        </Button>
      </CardContent>
    </Card>
  );
};

// Remedy Generator Component
const RemedyGenerator = ({ downloadAsPDF, downloadAsDOCX, hasLimitedRemedyAccess, hasPremiumAccess }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [documentVault, setDocumentVault] = useState([]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;

  // Download functions for PDF and DOC (if not provided as props)
  const localDownloadAsPDF = async (content, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/documents/download-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          content: content,
          filename: filename
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${filename}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('PDF generation failed');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const localDownloadAsDOCX = async (content, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/documents/download-docx`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          content: content,
          filename: filename
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${filename}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('DOCX generation failed');
      }
    } catch (error) {
      console.error('Error downloading DOCX:', error);
      alert('Error generating DOCX. Please try again.');
    }
  };

  // Use provided functions or fallback to local ones
  const finalDownloadAsPDF = downloadAsPDF || localDownloadAsPDF;
  const finalDownloadAsDOCX = downloadAsDOCX || localDownloadAsDOCX;

  // Administrative document download function
  const downloadAdministrativeDocument = async (documentName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/documents/download-administrative/${documentName}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        
        // Get filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get('content-disposition');
        let filename = 'Administrative_Document.docx';
        if (contentDisposition) {
          const matches = /filename="([^"]*)"/.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1];
          }
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('Administrative document download failed');
      }
    } catch (error) {
      console.error('Error downloading administrative document:', error);
      alert('Error downloading administrative document. Please try again.');
    }
  };

  // Template definitions with comprehensive legal templates
  const templates = [
    {
      id: 'conditional_acceptance',
      title: 'Notice of Conditional Acceptance',
      description: 'Assert conditional acceptance of claims while maintaining your rights and standing.',
      category: 'Administrative Process',
      fields: [
        { name: 'fullName', label: 'Full Name (as Beneficiary)', type: 'text', required: true },
        { name: 'address', label: 'Mailing Address', type: 'textarea', required: true },
        { name: 'respondentName', label: 'Respondent Name/Title', type: 'text', required: true },
        { name: 'respondentAddress', label: 'Respondent Address', type: 'textarea', required: true },
        { name: 'matterReference', label: 'Matter/Case Reference', type: 'text', required: true },
        { name: 'claimSummary', label: 'Summary of Claim Being Addressed', type: 'textarea', required: true },
        { name: 'jurisdiction', label: 'Preferred Jurisdiction', type: 'select', options: ['Common Law', 'Equity', 'Trust Law'], required: true },
        { name: 'dateOfIncident', label: 'Date of Incident/Notice', type: 'date', required: true }
      ]
    },
    {
      id: 'affidavit_status',
      title: 'Affidavit of Status / Truth',
      description: 'Establish your lawful status and capacity under oath.',
      category: 'Status Declaration',
      fields: [
        { name: 'fullName', label: 'Full Name (Living Man/Woman)', type: 'text', required: true },
        { name: 'address', label: 'Domicile Address', type: 'textarea', required: true },
        { name: 'birthPlace', label: 'Place of Birth', type: 'text', required: true },
        { name: 'birthDate', label: 'Date of Birth', type: 'date', required: true },
        { name: 'statusClaim', label: 'Status Being Claimed', type: 'textarea', required: true },
        { name: 'jurisdiction', label: 'Jurisdiction', type: 'select', options: ['Common Law', 'Equity', 'Natural Law'], required: true },
        { name: 'witnessName', label: 'Witness Name (if applicable)', type: 'text', required: false },
        { name: 'notaryRequired', label: 'Notarization Required', type: 'checkbox', required: false }
      ]
    },
    {
      id: 'traffic_discharge',
      title: 'Traffic Violation Discharge Notice',
      description: 'Challenge traffic violations using constitutional and common law principles.',
      category: 'Traffic Matters',
      fields: [
        { name: 'fullName', label: 'Full Legal Name', type: 'text', required: true },
        { name: 'address', label: 'Address', type: 'textarea', required: true },
        { name: 'citationNumber', label: 'Citation/Ticket Number', type: 'text', required: true },
        { name: 'issuingOfficer', label: 'Issuing Officer Name', type: 'text', required: true },
        { name: 'dateOfCitation', label: 'Date of Citation', type: 'date', required: true },
        { name: 'locationOfIncident', label: 'Location of Alleged Violation', type: 'text', required: true },
        { name: 'allegedViolation', label: 'Alleged Violation Code/Description', type: 'textarea', required: true },
        { name: 'courtName', label: 'Court Name', type: 'text', required: true },
        { name: 'challengeBasis', label: 'Constitutional Challenge Basis', type: 'textarea', required: true }
      ]
    },
    {
      id: 'writ_mandamus',
      title: 'Writ of Mandamus (Equity Enforcement)',
      description: 'Compel government officials to perform their lawful duties.',
      category: 'Equity Relief',
      fields: [
        { name: 'petitionerName', label: 'Petitioner Name', type: 'text', required: true },
        { name: 'petitionerAddress', label: 'Petitioner Address', type: 'textarea', required: true },
        { name: 'respondentOfficial', label: 'Government Official/Agency', type: 'text', required: true },
        { name: 'respondentTitle', label: 'Official Title', type: 'text', required: true },
        { name: 'dutyDescription', label: 'Description of Duty to be Performed', type: 'textarea', required: true },
        { name: 'legalBasis', label: 'Legal Basis for Duty', type: 'textarea', required: true },
        { name: 'demandDate', label: 'Date of Initial Demand', type: 'date', required: true },
        { name: 'harmSuffered', label: 'Harm Suffered by Non-Performance', type: 'textarea', required: true }
      ]
    },
    {
      id: 'cease_desist',
      title: 'Cease & Desist Notice',
      description: 'Formal notice to halt unlawful actions and claims with constitutional protections.',
      category: 'Protective Notice',
      fields: [
        { name: 'senderName', label: 'Your Full Name (Living Man/Woman)', type: 'text', required: true },
        { name: 'senderAddress', label: 'Private Mailing Location or General Post Office Address', type: 'textarea', required: true },
        { name: 'senderEmail', label: 'Email Address', type: 'email', required: false },
        { name: 'senderPhone', label: 'Phone Number', type: 'tel', required: false },
        { name: 'recipientName', label: 'Offending Party\'s Name or Entity', type: 'text', required: true },
        { name: 'recipientAddress', label: 'Their Mailing Address', type: 'textarea', required: true },
        { name: 'unlawfulActions', label: 'Specific Unlawful Actions (e.g., unauthorized use of name, threats, harassment, false claims, fraudulent billing)', type: 'textarea', required: true },
        { name: 'grievancesList', label: 'List of Grievances/Unlawful Conduct/Fraudulent Claims', type: 'textarea', required: true },
        { name: 'rightsInfringement', label: 'Infringement on Personal, Intellectual, or Proprietary Rights', type: 'textarea', required: false },
        { name: 'harassmentDetails', label: 'Harassment, Defamation, Extortion, or Coercion Details', type: 'textarea', required: false },
        { name: 'unlawfulCommunications', label: 'Unlawful Communications or Debt Collection Attempts', type: 'textarea', required: false },
        { name: 'additionalViolations', label: 'Other Applicable Codes/Statutes/Treaties Violated', type: 'textarea', required: false },
        { name: 'timeframe', label: 'Compliance Timeframe (default: 10 days)', type: 'text', value: '10 days', required: true }
      ]
    },
    {
      id: 'status_correction',
      title: 'Status Correction Letter to State Agencies',
      description: 'Correct your status in government records and databases.',
      category: 'Administrative Correction',
      fields: [
        { name: 'fullName', label: 'Full Legal Name', type: 'text', required: true },
        { name: 'address', label: 'Current Address', type: 'textarea', required: true },
        { name: 'agencyName', label: 'State Agency Name', type: 'text', required: true },
        { name: 'agencyAddress', label: 'Agency Address', type: 'textarea', required: true },
        { name: 'recordType', label: 'Type of Record to Correct', type: 'text', required: true },
        { name: 'currentStatus', label: 'Current Incorrect Status', type: 'text', required: true },
        { name: 'correctedStatus', label: 'Correct Status Being Claimed', type: 'text', required: true },
        { name: 'correctionBasis', label: 'Legal Basis for Correction', type: 'textarea', required: true },
        { name: 'supportingDocuments', label: 'Supporting Documents Available', type: 'textarea', required: false }
      ]
    },
    {
      id: 'property_claim',
      title: 'Private Property Claim / Land Patent Declaration',
      description: 'Assert superior title and property rights under land patent.',
      category: 'Property Rights',
      fields: [
        { name: 'claimantName', label: 'Property Claimant Name', type: 'text', required: true },
        { name: 'claimantAddress', label: 'Claimant Address', type: 'textarea', required: true },
        { name: 'propertyDescription', label: 'Legal Property Description', type: 'textarea', required: true },
        { name: 'propertyAddress', label: 'Property Address', type: 'text', required: true },
        { name: 'patentNumber', label: 'Land Patent Number (if known)', type: 'text', required: false },
        { name: 'patentDate', label: 'Patent Date', type: 'date', required: false },
        { name: 'titleSource', label: 'Source of Title Chain', type: 'textarea', required: true },
        { name: 'encumbranceChallenge', label: 'Encumbrances Being Challenged', type: 'textarea', required: true },
        { name: 'superiorRight', label: 'Basis for Superior Right', type: 'textarea', required: true }
      ]
    },
    {
      id: 'power_attorney',
      title: 'Power of Attorney',
      description: 'Grant specific powers to act on your behalf in legal matters.',
      category: 'Legal Authorization',
      fields: [
        { name: 'principalName', label: 'Principal (Grantor) Name', type: 'text', required: true },
        { name: 'principalAddress', label: 'Principal Address', type: 'textarea', required: true },
        { name: 'attorneyName', label: 'Attorney-in-Fact Name', type: 'text', required: true },
        { name: 'attorneyAddress', label: 'Attorney-in-Fact Address', type: 'textarea', required: true },
        { name: 'powersGranted', label: 'Specific Powers Granted', type: 'textarea', required: true },
        { name: 'limitations', label: 'Limitations on Powers', type: 'textarea', required: false },
        { name: 'effectiveDate', label: 'Effective Date', type: 'date', required: true },
        { name: 'expirationDate', label: 'Expiration Date (if applicable)', type: 'date', required: false },
        { name: 'durableProvision', label: 'Durable (survives incapacity)', type: 'checkbox', required: false }
      ]
    },
    {
      id: 'plea_abatement',
      title: 'Plea of Abatement',
      description: 'Challenge court jurisdiction and procedural defects with proper legal standing.',
      category: 'Jurisdictional Challenge',
      fields: [
        { name: 'defendantName', label: 'Defendant Name', type: 'text', required: true },
        { name: 'defendantAddress', label: 'Defendant Address', type: 'textarea', required: true },
        { name: 'courtName', label: 'Court Name', type: 'text', required: true },
        { name: 'caseNumber', label: 'Case Number', type: 'text', required: true },
        { name: 'jurisdictionalDefects', label: 'Jurisdictional Defects', type: 'textarea', required: true },
        { name: 'proceduralDefects', label: 'Procedural Defects', type: 'textarea', required: true },
        { name: 'legalBasisChallenge', label: 'Legal Basis for Challenge', type: 'textarea', required: true },
        { name: 'requestedRelief', label: 'Relief Requested', type: 'textarea', required: true }
      ]
    },
    {
      id: 'affidavit_genealogy',
      title: 'Affidavit of Genealogy / Lineage Proof',
      description: 'Establish your genealogical heritage and lawful claim to family name, estate, and inheritance.',
      category: 'Heritage & Status',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'birthDate', label: 'Date of Birth', type: 'date', required: true },
        { name: 'birthPlace', label: 'Place of Birth (City/Town, County/Parish, State/Province, Country)', type: 'text', required: true },
        { name: 'nationality', label: 'Lawful Nationality (e.g., American Indian, Tribal Nation, etc.)', type: 'text', required: true },
        { name: 'fatherName', label: 'Biological Father\'s Full Name', type: 'text', required: true },
        { name: 'fatherBirthDate', label: 'Father\'s Date of Birth', type: 'date', required: true },
        { name: 'fatherBirthPlace', label: 'Father\'s Place of Birth', type: 'text', required: true },
        { name: 'motherName', label: 'Biological Mother\'s Full Name (Maiden Name)', type: 'text', required: true },
        { name: 'motherBirthDate', label: 'Mother\'s Date of Birth', type: 'date', required: true },
        { name: 'motherBirthPlace', label: 'Mother\'s Place of Birth', type: 'text', required: true },
        { name: 'paternalGrandfather', label: 'Paternal Grandfather Full Name', type: 'text', required: false },
        { name: 'paternalGrandmother', label: 'Paternal Grandmother Full Maiden Name', type: 'text', required: false },
        { name: 'maternalGrandfather', label: 'Maternal Grandfather Full Name', type: 'text', required: false },
        { name: 'maternalGrandmother', label: 'Maternal Grandmother Full Maiden Name', type: 'text', required: false },
        { name: 'tribalHeritage', label: 'Tribal/National/Family Heritage', type: 'textarea', required: false },
        { name: 'lineageEvidence', label: 'Evidence of Lineage (describe documents available)', type: 'textarea', required: false },
        { name: 'affidavitPurpose', label: 'Purpose of This Affidavit', type: 'textarea', required: true },
        { name: 'state', label: 'State/Republic of', type: 'text', required: true },
        { name: 'county', label: 'County/Parish of', type: 'text', required: true }
      ]
    },
    {
      id: 'fee_schedule_rights',
      title: 'Fee Schedule for Rights Violations',
      description: 'Publish a fee schedule for violations of your Natural, Common Law, Constitutional, and Equitable Rights.',
      category: 'Protective Notice',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'unlawfulDetentionFee', label: 'Fee for Unlawful Detention/Arrest (default: $50,000)', type: 'text', required: false },
        { name: 'unlawfulSearchFee', label: 'Fee for Unlawful Search (default: $25,000)', type: 'text', required: false },
        { name: 'unlawfulSeizureFee', label: 'Fee for Unlawful Seizure (default: $100,000)', type: 'text', required: false },
        { name: 'dueProcessFee', label: 'Fee for Due Process Violation (default: $75,000)', type: 'text', required: false },
        { name: 'threatCoercionFee', label: 'Fee for Threat/Coercion (default: $25,000)', type: 'text', required: false },
        { name: 'religiousRightsFee', label: 'Fee for Religious/Cultural Rights Violation (default: $150,000)', type: 'text', required: false },
        { name: 'travelRightsFee', label: 'Fee for Travel Rights Denial (default: $100,000)', type: 'text', required: false },
        { name: 'forcedMedicalFee', label: 'Fee for Forced Medical Procedures (default: $250,000)', type: 'text', required: false },
        { name: 'excessiveForceFee', label: 'Fee for Excessive Force/Battery (default: $500,000)', type: 'text', required: false },
        { name: 'surveillanceFee', label: 'Fee for Unlawful Surveillance (default: $50,000)', type: 'text', required: false },
        { name: 'counselRightsFee', label: 'Fee for Denial of Right to Counsel (default: $100,000)', type: 'text', required: false },
        { name: 'paymentTerms', label: 'Payment Terms (default: 10 days)', type: 'text', required: false },
        { name: 'state', label: 'State/Republic of', type: 'text', required: true },
        { name: 'county', label: 'County/Parish of', type: 'text', required: true }
      ]
    },
    {
      id: 'notice_rescind_contract',
      title: 'Notice of Right to Rescind Contract',
      description: 'Rescind contracts entered into without full disclosure, under duress, or lacking lawful consideration.',
      category: 'Contract Law',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'domicile', label: 'Domiciled upon the land known as (State/Republic/Tribal Nation)', type: 'text', required: true },
        { name: 'otherPartyName', label: 'Other Party to the Contract (Company, Agency, Officer, or Individual)', type: 'text', required: true },
        { name: 'contractReference', label: 'Contract Reference (Name, Account/Policy Number, or Description)', type: 'text', required: true },
        { name: 'contractDate', label: 'Date of Alleged Contract', type: 'date', required: true },
        { name: 'rescissionGrounds', label: 'Grounds for Rescission (select applicable)', type: 'textarea', required: true, placeholder: 'E.g., Lack of full disclosure, Misrepresentation/fraud, Duress/coercion, Lack of lawful consideration, Constitutional violations' },
        { name: 'specificDefects', label: 'Specific Defects in Contract Formation', type: 'textarea', required: true },
        { name: 'complianceTimeframe', label: 'Compliance Timeframe (default: 10 days)', type: 'text', required: false },
        { name: 'state', label: 'State/Republic of', type: 'text', required: true },
        { name: 'county', label: 'County/Parish of', type: 'text', required: true }
      ]
    },
    {
      id: 'writ_mandamus_enhanced',
      title: 'Writ of Mandamus (Enhanced)',
      description: 'Compel government officials to perform their clear and non-discretionary duties as required by law.',
      category: 'Equity Relief',
      fields: [
        { name: 'petitionerName', label: 'Petitioner Full Name', type: 'text', required: true },
        { name: 'courtName', label: 'Court Name', type: 'text', required: true },
        { name: 'county', label: 'County/Parish', type: 'text', required: true },
        { name: 'state', label: 'State/Republic', type: 'text', required: true },
        { name: 'respondentName', label: 'Respondent Name(s) - Public Officer(s)/Agency', type: 'text', required: true },
        { name: 'respondentCapacity', label: 'Respondent Official Capacity/Title', type: 'text', required: true },
        { name: 'ministerialDuty', label: 'Clear Ministerial Duty to be Performed', type: 'textarea', required: true },
        { name: 'dutySource', label: 'Legal Source of Duty (statute, constitution, oath, etc.)', type: 'textarea', required: true },
        { name: 'failureDescription', label: 'Description of Respondent\'s Failure to Perform Duty', type: 'textarea', required: true },
        { name: 'noticesGiven', label: 'Previous Notices/Demands Given to Respondent', type: 'textarea', required: true },
        { name: 'injurySuffered', label: 'Injury/Harm Suffered by Non-Performance', type: 'textarea', required: true },
        { name: 'specificActRequested', label: 'Specific Act Requested from Court', type: 'textarea', required: true },
        { name: 'respondentAddress', label: 'Respondent Address for Service', type: 'textarea', required: true },
        { name: 'feeScheduleAttached', label: 'Fee Schedule Attached for Costs/Damages', type: 'checkbox', required: false }
      ]
    },
    {
      id: 'notice_liability',
      title: 'Notice of Liability',
      description: 'Establish liability for officials who violate your Natural, Common Law, Constitutional, and Equitable Rights.',
      category: 'Protective Notice',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'officerName', label: 'Name of Officer/Agency/Department', type: 'text', required: true },
        { name: 'officerAddress', label: 'Officer/Agency Address', type: 'textarea', required: true },
        { name: 'specificViolations', label: 'Specific Rights Violations or Concerns', type: 'textarea', required: true },
        { name: 'feeScheduleReference', label: 'Reference to Fee Schedule (attach if available)', type: 'text', required: false },
        { name: 'responseTimeframe', label: 'Response Timeframe (default: 10 days)', type: 'text', required: false },
        { name: 'state', label: 'State/Republic of', type: 'text', required: true },
        { name: 'county', label: 'County/Parish of', type: 'text', required: true }
      ]
    },
    {
      id: 'public_notice_declaration',
      title: 'Public Notice Declaration',
      description: 'Publish your lawful status, private affairs, and establish public record of your standing.',
      category: 'Legal Status',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'nationality', label: 'Lawful Nationality (e.g., American Indian, Tribal American, etc.)', type: 'text', required: true },
        { name: 'trustName', label: 'Name of Private Trust (if established)', type: 'text', required: false },
        { name: 'spouseName', label: 'Spouse\'s Full Name (if declaring marriage)', type: 'text', required: false },
        { name: 'marriageDeclaration', label: 'Declare Common Law Marriage', type: 'checkbox', required: false },
        { name: 'privateProperty', label: 'Description of Private Property/Assets', type: 'textarea', required: false },
        { name: 'additionalDeclarations', label: 'Additional Status Declarations', type: 'textarea', required: false },
        { name: 'responseTimeframe', label: 'Response Timeframe for Challenges (default: 30 days)', type: 'text', required: false },
        { name: 'state', label: 'State/Republic of', type: 'text', required: true },
        { name: 'county', label: 'County/Parish of', type: 'text', required: true }
      ]
    },
    {
      id: 'plea_release_conditional',
      title: 'Plea of Release & Conditional Acceptance',
      description: 'Challenge bail, bond, or surety obligations and demand release on recognizance.',
      category: 'Court Pleading',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'courtName', label: 'Court Name', type: 'text', required: true },
        { name: 'county', label: 'County/Parish', type: 'text', required: true },
        { name: 'state', label: 'State/Republic', type: 'text', required: true },
        { name: 'prosecutorName', label: 'Prosecutor/Agency Name', type: 'text', required: true },
        { name: 'caseDetails', label: 'Case Number/Details (if known)', type: 'text', required: false },
        { name: 'detentionReason', label: 'Alleged Reason for Detention', type: 'textarea', required: false },
        { name: 'bailAmount', label: 'Bail/Bond Amount Demanded (if any)', type: 'text', required: false },
        { name: 'additionalArguments', label: 'Additional Legal Arguments', type: 'textarea', required: false },
        { name: 'prosecutorAddress', label: 'Prosecutor/Clerk Address for Service', type: 'textarea', required: true }
      ]
    },
    {
      id: 'motion_dismiss_jurisdiction',
      title: 'Motion to Dismiss for Lack of Jurisdiction',
      description: 'Challenge court jurisdiction and demand dismissal based on jurisdictional defects.',
      category: 'Court Pleading',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'courtName', label: 'Court Name', type: 'text', required: true },
        { name: 'county', label: 'County/Parish', type: 'text', required: true },
        { name: 'state', label: 'State/Republic', type: 'text', required: true },
        { name: 'plaintiffName', label: 'Plaintiff/Prosecutor/Agency Name', type: 'text', required: true },
        { name: 'caseNumber', label: 'Case Number (if known)', type: 'text', required: false },
        { name: 'chargeComplaint', label: 'Nature of Alleged Charge/Complaint', type: 'textarea', required: true },
        { name: 'jurisdictionDefects', label: 'Specific Jurisdictional Defects', type: 'textarea', required: true },
        { name: 'missingElements', label: 'Missing Elements (contract, injured party, etc.)', type: 'textarea', required: true },
        { name: 'plaintiffAddress', label: 'Plaintiff/Prosecutor Address for Service', type: 'textarea', required: true }
      ]
    },
    {
      id: 'petition_habeas_corpus',
      title: 'Petition for Writ of Habeas Corpus',
      description: 'Challenge unlawful detention and demand immediate release from custody.',
      category: 'Equity Relief',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'courtName', label: 'Court Name', type: 'text', required: true },
        { name: 'county', label: 'County/Parish', type: 'text', required: true },
        { name: 'state', label: 'State/Republic', type: 'text', required: true },
        { name: 'detainingAuthority', label: 'Name of Sheriff/Warden/Detaining Authority', type: 'text', required: true },
        { name: 'detentionDate', label: 'Date of Detention/Arrest', type: 'date', required: true },
        { name: 'detentionLocation', label: 'Current Location of Detention', type: 'text', required: true },
        { name: 'detentionCircumstances', label: 'Circumstances of Detention/Arrest', type: 'textarea', required: true },
        { name: 'dueProcessViolations', label: 'Due Process Violations', type: 'textarea', required: true },
        { name: 'lawfulCause', label: 'Lack of Lawful Cause for Detention', type: 'textarea', required: true },
        { name: 'respondentAddress', label: 'Respondent Address for Service', type: 'textarea', required: true }
      ]
    },
    {
      id: 'notice_liability_enhanced',
      title: 'Notice of Liability (Enhanced)',
      description: 'Establish personal and commercial liability for officials violating Natural, Common Law, Constitutional, and Equitable Rights.',
      category: 'Protective Notice',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'mailingAddress', label: 'Mailing Location, Non-Domestic (C/O Address)', type: 'textarea', required: true },
        { name: 'officerName', label: 'Name of Officer/Agency/Department', type: 'text', required: true },
        { name: 'officerAddress', label: 'Officer/Agency Address', type: 'textarea', required: true },
        { name: 'specificViolations', label: 'Specific Rights Violations or Potential Violations', type: 'textarea', required: true },
        { name: 'feeScheduleAttached', label: 'Fee Schedule Attached', type: 'checkbox', required: false },
        { name: 'responseTimeframe', label: 'Response Timeframe (default: 10 days)', type: 'text', required: false }
      ]
    },
    {
      id: 'notice_tender_payment',
      title: 'Notice of Tender of Lawful Payment',
      description: 'Tender lawful payment to settle and discharge alleged debts or obligations.',
      category: 'Debt Discharge',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'creditorName', label: 'Creditor/Claimant/Agency Name', type: 'text', required: true },
        { name: 'creditorAddress', label: 'Creditor Address', type: 'textarea', required: true },
        { name: 'debtAmount', label: 'Alleged Debt Amount', type: 'text', required: false },
        { name: 'accountNumber', label: 'Account/Claim/Case Number', type: 'text', required: false },
        { name: 'paymentForm', label: 'Form of Lawful Payment', type: 'select', 
          options: [
            'Lawful Money Demand under Title 12 USC § 411',
            'Bill of Exchange',
            'Bond for Discharge',
            'Promissory Note',
            'Other Lawful Instrument'
          ], required: true },
        { name: 'responseTimeframe', label: 'Response Timeframe (default: 10 days)', type: 'text', required: false },
        { name: 'state', label: 'State/Republic of', type: 'text', required: true },
        { name: 'county', label: 'County/Parish of', type: 'text', required: true }
      ]
    },
    {
      id: 'affidavit_discharge_debt',
      title: 'Affidavit of Discharge of Debt',
      description: 'Record formal discharge of debt after tender of lawful payment and creditor failure to rebut.',
      category: 'Debt Discharge',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'creditorName', label: 'Creditor/Claimant/Agency Name', type: 'text', required: true },
        { name: 'debtAmount', label: 'Original Alleged Debt Amount', type: 'text', required: true },
        { name: 'debtDate', label: 'Date Debt Was Alleged', type: 'date', required: true },
        { name: 'tenderDate', label: 'Date Lawful Payment Was Tendered', type: 'date', required: true },
        { name: 'tenderForm', label: 'Form of Tender Used', type: 'text', required: true },
        { name: 'creditorResponse', label: 'Creditor Response (if any)', type: 'textarea', required: false },
        { name: 'accountNumber', label: 'Account/Claim Number', type: 'text', required: false },
        { name: 'state', label: 'State/Republic of', type: 'text', required: true },
        { name: 'county', label: 'County/Parish of', type: 'text', required: true }
      ]
    },
    {
      id: 'bill_exchange_discharge',
      title: 'Bill of Exchange / Bond for Discharge',
      description: 'Issue negotiable instrument as lawful tender for debt discharge under Article I Section 10.',
      category: 'Commercial Instruments',
      fields: [
        { name: 'drawerName', label: 'Drawer (Your Full Name)', type: 'text', required: true },
        { name: 'drawerAddress', label: 'Drawer Mailing Location (C/O Address)', type: 'textarea', required: true },
        { name: 'draweeName', label: 'Drawee (Creditor/Agency/Claimant)', type: 'text', required: true },
        { name: 'draweeAddress', label: 'Drawee Address', type: 'textarea', required: true },
        { name: 'payeeName', label: 'Payee (Beneficiary/Holder of Claim)', type: 'text', required: true },
        { name: 'instrumentAmount', label: 'Amount (in lawful money of account)', type: 'text', required: true },
        { name: 'accountNumber', label: 'Account/Claim/Case Number', type: 'text', required: true },
        { name: 'obligationDescription', label: 'Description of Alleged Obligation', type: 'textarea', required: false }
      ]
    },
    {
      id: 'notice_setoff_counterclaim',
      title: 'Notice of Set-Off & Counterclaim',
      description: 'Set-off alleged claims and assert counterclaim for dishonor, harassment, and rights violations.',
      category: 'Commercial Defense',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'mailingAddress', label: 'Mailing Location (C/O Address)', type: 'textarea', required: true },
        { name: 'creditorName', label: 'Creditor/Agency/Claimant Name', type: 'text', required: true },
        { name: 'creditorAddress', label: 'Creditor Address', type: 'textarea', required: true },
        { name: 'originalClaimAmount', label: 'Amount of Original Alleged Claim', type: 'text', required: true },
        { name: 'accountNumber', label: 'Account/Claim/Case Number', type: 'text', required: false },
        { name: 'tenderDate', label: 'Date Bill of Exchange/Bond Was Issued', type: 'date', required: true },
        { name: 'creditorViolations', label: 'Creditor Violations (dishonor, harassment, etc.)', type: 'textarea', required: true },
        { name: 'counterclaimAmount', label: 'Counterclaim Amount (e.g., $500,000)', type: 'text', required: true },
        { name: 'responseTimeframe', label: 'Response Timeframe (default: 10 days)', type: 'text', required: false }
      ]
    },
    {
      id: 'affidavit_treasonous_allegiance',
      title: 'Affidavit of Treasonous Allegiance',
      description: 'Challenge BAR attorneys and judges on foreign allegiance, oath violations, and potential treason.',
      category: 'Jurisdictional Challenge',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'respondentName', label: 'Attorney\'s/Judge\'s Full Name', type: 'text', required: true },
        { name: 'respondentTitle', label: 'Respondent\'s Title/Position', type: 'text', required: false },
        { name: 'barAssociation', label: 'BAR Association/State Bar', type: 'text', required: false },
        { name: 'caseContext', label: 'Case/Matter Context (if applicable)', type: 'textarea', required: false },
        { name: 'specificViolations', label: 'Specific Constitutional/Oath Violations Observed', type: 'textarea', required: false },
        { name: 'foreignAllegiance', label: 'Evidence of Foreign Allegiance/FARA Violations', type: 'textarea', required: false },
        { name: 'responseTimeframe', label: 'Response Timeframe (default: 10 days)', type: 'text', required: false },
        { name: 'state', label: 'State/Republic of', type: 'text', required: true },
        { name: 'county', label: 'County/Parish of', type: 'text', required: true }
      ]
    },
    {
      id: 'notice_postal_authority',
      title: 'Notice of Postal Authority & Affidavit of Seal',
      description: 'Establish postal authority and private seal using postage stamps for lawful commercial instruments.',
      category: 'Commercial Authority',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'stampValue', label: 'Postage Stamp Value (e.g., $2.00, $5.00)', type: 'text', required: false },
        { name: 'refusalFee', label: 'Fee for Refusal to Recognize Seal (default: $1,000.00)', type: 'text', required: false },
        { name: 'dishonorFee', label: 'Fee for Dishonor/Fraud in Commerce (default: $5,000.00)', type: 'text', required: false },
        { name: 'responseTimeframe', label: 'Response Timeframe (default: 10 days)', type: 'text', required: false },
        { name: 'city', label: 'City of Execution', type: 'text', required: true },
        { name: 'state', label: 'State/Republic of', type: 'text', required: true }
      ]
    },
    {
      id: 'notice_liability_mailing',
      title: 'Notice of Liability & Affidavit of Proper Mailing Location',
      description: 'Establish proper mailing location and liability for misdirected correspondence or mail fraud.',
      category: 'Administrative Notice',
      fields: [
        { name: 'fullName', label: 'Full Given Name, Family Name', type: 'text', required: true },
        { name: 'properMailingAddress', label: 'Proper Mailing Location (General Delivery or C/O Address)', type: 'textarea', required: true },
        { name: 'city', label: 'City', type: 'text', required: true },
        { name: 'state', label: 'State/Republic', type: 'text', required: true },
        { name: 'zipCode', label: 'ZIP Code', type: 'text', required: true },
        { name: 'misdirectedFee', label: 'Fee for Misdirected Mailing (default: $500.00)', type: 'text', required: false },
        { name: 'enforcementFee', label: 'Fee for Improper Enforcement (default: $1,000.00)', type: 'text', required: false },
        { name: 'fraudFee', label: 'Fee for Fraud/Dishonor (default: $5,000.00)', type: 'text', required: false },
        { name: 'responseTimeframe', label: 'Response Timeframe (default: 10 days)', type: 'text', required: false }
      ]
    },
    {
      id: 'notice_rescission',
      title: 'Notice of Rescission',
      description: 'Enforce the principles of Equity Jurisprudence and English Common Law by formally rescinding instruments and presumed consent.',
      category: 'Equity Jurisprudence',
      fields: [
        { name: 'fullName', label: 'Full Legal Name', type: 'text', required: true },
        { name: 'county', label: 'County/State/Country', type: 'text', required: true },
        { name: 'respondentNames', label: 'Full Legal Name(s) of agency/officer/corporation/individual', type: 'text', required: true },
        { name: 'matterReference', label: 'Account/Contract/Case/Invoice/Notice No.', type: 'text', required: true },
        { name: 'matterDate', label: 'Date(s) of Related Matter', type: 'date', required: true },
        { name: 'instrumentDescription', label: 'Instrument/Agreement/Document Being Rescinded', type: 'textarea', required: true },
        { name: 'instrumentDate', label: 'Date Executed/Issued', type: 'date', required: true },
        { name: 'partiesNamed', label: 'Parties Named on Instrument', type: 'text', required: true },
        { name: 'rescissionGrounds', label: 'Specific Grounds for Rescission', type: 'textarea', required: true, placeholder: 'E.g., Lack of full disclosure, Absence of meeting of minds, Failure of consideration, Duress/coercion, etc.' },
        { name: 'additionalGrounds', label: 'Additional Specific Grounds (with dates and facts)', type: 'textarea', required: false },
        { name: 'respondentAddress', label: 'Respondent Address', type: 'textarea', required: true },
        { name: 'counselAddress', label: 'Counsel/Agent Address (if applicable)', type: 'textarea', required: false },
        { name: 'mailingAddress', label: 'Your Mailing Address', type: 'textarea', required: true },
        { name: 'email', label: 'Email Address', type: 'email', required: false },
        { name: 'phone', label: 'Phone Number', type: 'tel', required: false }
      ]
    },
    {
      id: 'bill_of_complaint',
      title: 'Bill of Complaint',
      description: 'Verified Complaint in Equity for Breach of Trust, Accounting, Removal of Trustee, and Related Relief. Use this to bring action in a Court of Chancery/Equity against a trustee who has breached fiduciary duties.',
      category: 'Trust & Equity',
      fields: [
        { name: 'stateName', label: 'State Name', type: 'text', required: true, placeholder: 'e.g., Delaware' },
        { name: 'countyName', label: 'County Name', type: 'text', required: true },
        { name: 'plaintiffName', label: 'Plaintiff\'s Full Legal Name', type: 'text', required: true },
        { name: 'plaintiffCity', label: 'Plaintiff\'s City', type: 'text', required: true },
        { name: 'plaintiffCounty', label: 'Plaintiff\'s County', type: 'text', required: true },
        { name: 'plaintiffState', label: 'Plaintiff\'s State', type: 'text', required: true },
        { name: 'trustName', label: 'Name of Trust', type: 'text', required: true, placeholder: 'e.g., The Smith Family Trust' },
        { name: 'trustInstrumentDate', label: 'Trust Instrument Date', type: 'date', required: true },
        { name: 'settlorName', label: 'Settlor\'s Full Name (Creator of Trust)', type: 'text', required: true },
        { name: 'defendantName', label: 'Defendant (Trustee) Full Legal Name', type: 'text', required: true },
        { name: 'defendantCity', label: 'Defendant\'s City', type: 'text', required: true },
        { name: 'defendantCounty', label: 'Defendant\'s County', type: 'text', required: true },
        { name: 'defendantState', label: 'Defendant\'s State', type: 'text', required: true },
        { name: 'trusteeAppointmentDate', label: 'Date Defendant Became Trustee', type: 'date', required: true },
        { name: 'trustDispositions', label: 'Key Trust Provisions (income/principal distributions, etc.)', type: 'textarea', required: true, placeholder: 'Summarize key dispositive provisions; e.g., income and/or principal to be distributed to Plaintiff; remainder beneficiaries, etc.' },
        { name: 'trustPropertyCash', label: 'Trust Cash/Securities Value (approximate)', type: 'text', required: false, placeholder: 'e.g., $500,000' },
        { name: 'trustPropertyReal', label: 'Trust Real Property (address/description)', type: 'textarea', required: false },
        { name: 'trustPropertyOther', label: 'Other Trust Assets', type: 'textarea', required: false },
        { name: 'misappropriationAmount', label: 'Amount Misappropriated (if any)', type: 'text', required: false, placeholder: 'e.g., $50,000' },
        { name: 'misappropriationDates', label: 'Dates of Misappropriation', type: 'text', required: false },
        { name: 'misappropriationRecipient', label: 'Recipient of Misappropriated Funds', type: 'text', required: false, placeholder: 'himself/herself, or entity name' },
        { name: 'imprudentInvestment', label: 'Description of Imprudent Investment (if any)', type: 'textarea', required: false },
        { name: 'accountingRequestDates', label: 'Dates of Accounting Requests', type: 'text', required: false },
        { name: 'unauthorizedDistributions', label: 'Description of Unauthorized Distributions', type: 'textarea', required: false },
        { name: 'otherMismanagement', label: 'Other Mismanagement Details', type: 'textarea', required: false, placeholder: 'e.g., failing to pay property taxes, allowing property to fall into disrepair, commingling funds, etc.' },
        { name: 'conditionalAcceptanceDate', label: 'Date of Conditional Acceptance Notice', type: 'date', required: false },
        { name: 'conditionalAcceptanceMethod', label: 'Method of Service (certified mail, personal service, etc.)', type: 'text', required: false },
        { name: 'stateStatute', label: 'State Statute/Trust Code Citation', type: 'text', required: false, placeholder: 'e.g., 12 Del. C. § 3501' },
        { name: 'counselName', label: 'Counsel Name (if any)', type: 'text', required: false },
        { name: 'counselBarNumber', label: 'Counsel Bar Number', type: 'text', required: false },
        { name: 'counselAddress', label: 'Counsel Address', type: 'textarea', required: false },
        { name: 'counselPhone', label: 'Counsel Phone', type: 'tel', required: false },
        { name: 'counselEmail', label: 'Counsel Email', type: 'email', required: false }
      ]
    }
  ];

  // Load document vault on component mount
  useEffect(() => {
    const loadDocumentVault = () => {
      const saved = localStorage.getItem(`document_vault_${user?.email}`);
      if (saved) {
        setDocumentVault(JSON.parse(saved));
      }
    };
    if (user?.email) {
      loadDocumentVault();
    }
  }, [user?.email]);

  // Save document to vault
  const saveToVault = (document) => {
    const newVault = [...documentVault, {
      ...document,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      userEmail: user?.email
    }];
    setDocumentVault(newVault);
    localStorage.setItem(`document_vault_${user?.email}`, JSON.stringify(newVault));
  };

  // Handle form input changes
  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Generate document using template
  const generateDocument = async () => {
    setIsGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/documents/generate-remedy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          templateId: selectedTemplate.id,
          formData: formData,
          templateInfo: selectedTemplate
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Document generation successful:', result);
        setGeneratedDocument(result);
        setCurrentStep(3);
        
        // Save to vault automatically
        saveToVault({
          templateId: selectedTemplate.id,
          templateTitle: selectedTemplate.title,
          formData: formData,
          generatedContent: result.content,
          documentId: result.document_id  // Backend returns document_id with underscore
        });
      } else {
        const errorData = await response.text();
        console.error('Document generation failed:', errorData);
        throw new Error(`Document generation failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Error generating document:', error);
      alert('Error generating document. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Reset to step 1
  const resetGenerator = () => {
    setCurrentStep(1);
    setSelectedTemplate(null);
    setFormData({});
    setGeneratedDocument(null);
  };

  if (currentStep === 1) {
    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Remedy Generator</h2>
          <p className="text-gray-600">"You fill in the truth, we build the law around it."</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates
            .filter(template => {
              // If user has limited remedy access (Pro Se Starter), only show specific templates
              if (hasLimitedRemedyAccess) {
                return ['cease_desist', 'power_attorney'].includes(template.id);
              }
              // Premium users see all templates
              return hasPremiumAccess;
            })
            .map((template) => (
            <Card 
              key={template.id} 
              className="hover:shadow-lg transition-shadow duration-200 cursor-pointer border-2 hover:border-purple-200"
              onClick={() => {
                setSelectedTemplate(template);
                setCurrentStep(2);
              }}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
                    <Gavel className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg text-gray-900 mb-1">{template.title}</CardTitle>
                    <Badge variant="outline" className="text-xs mb-2">
                      {template.category}
                    </Badge>
                    <p className="text-sm text-gray-600">{template.description}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Document
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (currentStep === 2 && selectedTemplate) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{selectedTemplate.title}</h2>
            <p className="text-gray-600">{selectedTemplate.description}</p>
          </div>
          <Button variant="outline" onClick={resetGenerator}>
            ← Back to Templates
          </Button>
        </div>

        <div className="bg-white rounded-lg border p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">Fill in the Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {selectedTemplate.fields.map((field) => (
              <div key={field.name} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                
                {field.type === 'text' && (
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                  />
                )}
                
                {field.type === 'textarea' && (
                  <textarea
                    rows="4"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                  />
                )}
                
                {field.type === 'date' && (
                  <input
                    type="date"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                  />
                )}
                
                {field.type === 'select' && (
                  <select
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                  >
                    <option value="">Select {field.label}</option>
                    {field.options.map((option) => {
                      // Handle both string options and {value, label} object options
                      const optionValue = typeof option === 'object' ? option.value : option;
                      const optionLabel = typeof option === 'object' ? option.label : option;
                      return (
                        <option key={optionValue} value={optionValue}>
                          {optionLabel}
                        </option>
                      );
                    })}
                  </select>
                )}
                
                {field.type === 'checkbox' && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      checked={formData[field.name] || false}
                      onChange={(e) => handleInputChange(field.name, e.target.checked)}
                    />
                    <label className="ml-2 text-sm text-gray-700">Yes</label>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t">
            <Button variant="outline" onClick={resetGenerator}>
              Cancel
            </Button>
            <Button 
              onClick={generateDocument}
              disabled={isGenerating}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Generate Document
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 3 && generatedDocument) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Generated Document</h2>
            <p className="text-gray-600">{selectedTemplate.title}</p>
          </div>
          <div className="space-x-2">
            <Button variant="outline" onClick={resetGenerator}>
              Create Another
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg border">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Document Preview</h3>
              <div className="space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => finalDownloadAsPDF(generatedDocument.content, `${selectedTemplate.title.replace(/[^a-zA-Z0-9]/g, '_')}`)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => finalDownloadAsDOCX(generatedDocument.content, `${selectedTemplate.title.replace(/[^a-zA-Z0-9]/g, '_')}`)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download DOCX
                </Button>
                <Button variant="outline" size="sm">
                  <Mail className="w-4 h-4 mr-2" />
                  Send via Mail
                </Button>
              </div>
            </div>
          </div>
          
          <div className="p-6 max-h-96 overflow-y-auto">
            <div 
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: generatedDocument.content || 'Document content loading...' }}
            />
          </div>
        </div>

        {/* EXECUTING THE ADMINISTRATIVE PROCESS Section */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <Gavel className="w-6 h-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-bold text-gray-900">EXECUTING THE ADMINISTRATIVE PROCESS</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Complete your administrative remedy with these three essential documents. Download and execute in sequence for maximum effectiveness.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Document 1 - Certificate of Non-Response */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="text-sm font-semibold text-blue-600">STEP 1</span>
                  </div>
                  <Badge className="bg-green-100 text-green-800 text-xs">Essential</Badge>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Certificate of Non-Response and Admission of Facts</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Establishes non-response as admission when the opposing party fails to rebut your claims within the specified timeframe.
                </p>
                <Button 
                  className="w-full" 
                  size="sm" 
                  onClick={() => downloadAdministrativeDocument('certificate_non_response')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download DOCX
                </Button>
              </div>

              {/* Document 2 - Notice of Discharge */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="text-sm font-semibold text-blue-600">STEP 2</span>
                  </div>
                  <Badge className="bg-yellow-100 text-yellow-800 text-xs">Critical</Badge>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Notice of Discharge to Court</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Formal notice to the court declaring the discharge of the matter based on administrative closure and non-response.
                </p>
                <Button 
                  className="w-full" 
                  size="sm" 
                  onClick={() => downloadAdministrativeDocument('notice_discharge')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download DOCX
                </Button>
              </div>

              {/* Document 3 - Final Administrative Closure */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="text-sm font-semibold text-blue-600">STEP 3</span>
                  </div>
                  <Badge className="bg-red-100 text-red-800 text-xs">Final</Badge>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Final Administrative Closure Notice</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Concludes the administrative process and establishes the final remedy based on the completed administrative procedure.
                </p>
                <Button 
                  className="w-full" 
                  size="sm" 
                  onClick={() => downloadAdministrativeDocument('final_closure')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download DOCX
                </Button>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                <div>
                  <h5 className="font-semibold text-blue-900 mb-1">Administrative Process Guidelines</h5>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Execute documents in sequential order (Steps 1-3)</li>
                    <li>• Allow proper time frames between each document</li>
                    <li>• Maintain proper service and filing procedures</li>
                    <li>• Keep detailed records of all administrative actions</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SovereignToolbox;